import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { client } from '../../api/Api';
import { Link } from 'react-router-dom';

export default function AdminPgvector() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  const [searchText, setSearchText] = useState('');
  const [searchLimit, setSearchLimit] = useState(10);
  const [searchResults, setSearchResults] = useState([]);
  const [opMsg, setOpMsg] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / size)), [total, size]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const res = await client.get('/admin/pgvector/products', { params: { q, page, size } });
      const data = res.data || {};
      setItems(Array.isArray(data.content) ? data.content : []);
      setTotal(Number(data.totalElements || 0));
    } catch (e) {
      setOpMsg(e?.message || 'Nepavyko įkelti produktų');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page, size]);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const allSelectedIds = useMemo(() => Object.entries(selected).filter(([, v]) => v).map(([k]) => Number(k)), [selected]);

  const indexSelected = async () => {
    if (allSelectedIds.length === 0) { setOpMsg('Pasirinkite bent vieną produktą'); return; }
    setOpMsg('');
    try {
      const res = await client.post('/admin/pgvector/index', { ids: allSelectedIds });
      const d = res.data;
      setOpMsg(d?.success ? `Sėkmingai indeksuota: ${d.indexed}, klaidų: ${d.failed}` : (d?.error || 'Nepavyko indeksuoti'));
    } catch (e) {
      setOpMsg(e?.message || 'Nepavyko indeksuoti');
    }
  };

  const runSemanticSearch = async () => {
    if (!searchText.trim()) { setOpMsg('Įveskite paieškos tekstą'); return; }
    setOpMsg('');
    try {
      const res = await client.post('/admin/pgvector/search', { text: searchText, limit: Number(searchLimit) || 10 });
      const d = res.data || {};
      if (d.success) setSearchResults(Array.isArray(d.items) ? d.items : []);
      else setOpMsg(d.error || 'Paieška nepavyko');
    } catch (e) {
      setOpMsg(e?.message || 'Paieška nepavyko');
    }
  };

  return (
    <div className="space-y-6">
      <nav className="text-sm" aria-label="Breadcrumb">
        <ol className="mb-2 flex items-center gap-2 text-slate-500">
          <li><Link to="/admin/dashboard" className="hover:text-slate-700">Admin</Link></li>
          <li aria-hidden className="text-slate-300">/</li>
          <li className="text-slate-700">pgvector</li>
        </ol>
      </nav>

      <div className="rounded-md border p-4">
        <h2 className="font-semibold mb-3">Produktų pasirinkimas ir indeksavimas į pgvector</h2>
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <input className="border rounded px-2 py-1" placeholder="Paieška pagal pavadinimą" value={q} onChange={e => { setPage(0); setQ(e.target.value); }} />
          <select className="border rounded px-2 py-1" value={size} onChange={e => { setPage(0); setSize(Number(e.target.value)); }}>
            {[10,20,50,100].map(s => <option key={s} value={s}>{s}/psl</option>)}
          </select>
          <span className="text-sm text-slate-500 ml-auto">Iš viso: {total}</span>
        </div>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Kraunama</Typography>
          </Box>
        )}
        {!loading && (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-2">Pasirinkti</th>
                  <th className="py-2 pr-2">ID</th>
                  <th className="py-2 pr-2">Pavadinimas</th>
                  <th className="py-2 pr-2">EN</th>
                  <th className="py-2 pr-2">Alias</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 pr-2"><input type="checkbox" checked={!!selected[it.id]} onChange={() => toggle(it.id)} /></td>
                    <td className="py-2 pr-2">{it.id}</td>
                    <td className="py-2 pr-2">{it.name}</td>
                    <td className="py-2 pr-2">{it.nameEN || ''}</td>
                    <td className="py-2 pr-2">{it.alias || ''}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td className="py-4 text-slate-500" colSpan={5}>Nieko nerasta</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button className="px-3 py-1 border rounded" disabled={page<=0} onClick={() => setPage(p => Math.max(0, p-1))}>Ankstesnis</button>
          <span className="text-sm">{page+1} / {totalPages}</span>
          <button className="px-3 py-1 border rounded" disabled={page+1>=totalPages} onClick={() => setPage(p => p+1)}>Kitas</button>
          <div className="flex-1" />
          <button className="px-3 py-1 bg-emerald-600 text-white rounded" onClick={indexSelected}>Indeksuoti pasirinktus</button>
          <button className="px-3 py-1 bg-purple-600 text-white rounded ml-2" onClick={async ()=>{
            try { setOpMsg(''); const res= await client.post('/admin/pgvector/index-missing', { limit: 2000 }); const d=res.data; setOpMsg(d?.success?`Trūkstamų indeksavimas: įdėta ${d.indexed}, jau buvo ${d.alreadyIndexed}, be EN ${d.skippedNoEN}, klaidų ${d.errors}`:(d?.error||'Nepavyko paleisti indeksavimo')); }
            catch(e){ setOpMsg(e?.message||'Nepavyko paleisti indeksavimo'); }
          }}>Indeksuoti trūkstamus (EN tik)</button>
        </div>
      </div>

      <div className="rounded-md border p-4">
        <h2 className="font-semibold mb-3">Semantinė paieška pgvector</h2>
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <input className="border rounded px-2 py-1 flex-1 min-w-[260px]" placeholder="Paieškos tekstas" value={searchText} onChange={e => setSearchText(e.target.value)} />
          <input className="border rounded px-2 py-1 w-24" type="number" min={1} max={50} value={searchLimit} onChange={e => setSearchLimit(e.target.value)} />
          <button className="px-3 py-1 bg-blue-600 text-white rounded" onClick={runSemanticSearch}>Ieškoti</button>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-2">ID</th>
                <th className="py-2 pr-2">Score</th>
                <th className="py-2 pr-2">Distance</th>
                <th className="py-2 pr-2">Pavadinimas</th>
                <th className="py-2 pr-2">EN</th>
                <th className="py-2 pr-2">Alias</th>
                <th className="py-2 pr-2">Indeksuotas tekstas</th>
              </tr>
            </thead>
            <tbody>
              {searchResults.map(it => (
                <tr key={it.id} className="border-b hover:bg-slate-50">
                  <td className="py-2 pr-2">{it.id}</td>
                  <td className="py-2 pr-2">{typeof it.score === 'number' ? it.score.toFixed(4) : ''}</td>
                  <td className="py-2 pr-2">{typeof it.distance === 'number' ? it.distance.toFixed(4) : ''}</td>
                  <td className="py-2 pr-2">{it.name}</td>
                  <td className="py-2 pr-2">{it.nameEN || ''}</td>
                  <td className="py-2 pr-2">{it.alias || ''}</td>
                  <td className="py-2 pr-2 max-w-[420px] truncate" title={it.indexedText || ''}>{it.indexedText || ''}</td>
                </tr>
              ))}
              {searchResults.length === 0 && <tr><td className="py-4 text-slate-500" colSpan={7}>Rezultatų nėra</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {opMsg && <div className="rounded border p-3 text-sm">{opMsg}</div>}
    </div>
  );
}
